/**
 * DataSource: Instrumentação (PZ, INA, MS, TILT, etc.)
 * ----------------------------------------------------
 * Hoje: leitura manual via UI → grava na tabela `readings` do Supabase.
 * Futuro: webhook `ingest-tetratech` recebe leituras da API TetraTech
 *         e popula a mesma tabela. UI continua lendo do Supabase.
 *
 * Telles (comentário): "os dados de instrumentos serão tratados pelo
 * aplicativo via API a partir do servidor de chegada dos dados".
 */

import { supabase } from '@/lib/supabase';
import type { Canal, Instrument, InstrumentId, NewReadingInput, Reading, DamId } from '@/domain';
import { asDamId, asEngineerId, asInstrumentId, asReadingId } from '@/domain';
import type { FaixaCanal } from '@/domain/instrumentos/tilt';
import type { DataSourceMeta } from './types';

// ── Contrato ────────────────────────────────────────────────
export interface InstrumentacaoDataSource extends DataSourceMeta {
  listInstruments(opts?: { damId?: DamId }): Promise<Instrument[]>;
  getInstrument(id: InstrumentId): Promise<Instrument | null>;
  /** `canal` filtra a grandeza — obrigatório para instrumento multi-canal, senão a
   *  série do tiltímetro vem com eixo A, eixo B e temperatura embaralhados e o
   *  `limit` rende um terço dos pontos que aparenta (GUPM-270). */
  listLatestReadings(opts: {
    instrumentId?: InstrumentId; damId?: DamId; limit?: number; canal?: Canal | null;
  }): Promise<Reading[]>;
  /** Faixas vigentes por canal (view `v_faixas_canal`) — régua do tiltímetro por eixo. */
  listFaixasCanal(opts?: { instrumentId?: InstrumentId }): Promise<FaixaCanal[]>;
  /** Última leitura POR instrumento (view `readings_current`). Ao contrário de
   *  `listLatestReadings` (top-N global), não perde instrumento de baixa cadência.
   *  Filtros opcionais (simetria com `listLatestReadings`): por instrumento ou barragem. */
  listCurrentReadings(opts?: { instrumentId?: InstrumentId; damId?: DamId }): Promise<Reading[]>;
  submitReading(input: NewReadingInput): Promise<Reading>;
}

// ── Implementação POC (manual via Supabase) ─────────────────
/**
 * Linha de `v_mapa_instrumentos` (GUPM-227): `instruments` + carta vigente de
 * `instrument_thresholds`. `gpx_mapa` foi dropada — o coletor `/mapa` está
 * descontinuado e o snapshot era de 06/07/2026.
 *
 * Coordenada do pino vem de `easting`/`northing` de `instruments`, alimentadas
 * pelo inventário v2. Os nomes `param_*` foram preservados na view de propósito:
 * o significado é o mesmo que vinha do mapa (o ponto em que cada nível começa),
 * só a fonte mudou — ver `20260803120000_gpx_only_aposenta_mapa.sql`.
 */
function rowToInstrument(r: {
  id: string; dam_id: string; code: string; type: Instrument['type']; label: string;
  level_normal_max: number | null;
  thresholds_valid_from: string; thresholds_source: string;
  section: string | null; cota_topo: number | null; cota_fundo: number | null; profundidade: number | null;
  modo_leitura: Instrument['modoLeitura']; gpx_area: string | null; gpx_estrutura: string | null;
  is_active: boolean; data_class: Instrument['dataClass'];
  is_online: boolean | null; last_reading_at: string | null;
  fora_de_servico_desde: string | null; fora_de_servico_motivo: string | null;
  easting: number | null; northing: number | null;
  param_atencao: number | null; param_alerta: number | null; param_emergencia: number | null;
  lat: number | null; lon: number | null;
  canais_com_carta: Canal[] | null;
}): Instrument {
  // GUPM-270 — "tem carta" passou a incluir a carta POR CANAL. Antes só olhava os
  // `param_*` (a carta escalar), e o tiltímetro não tem nenhuma: com as 20 faixas por
  // eixo carregadas, ele continuava chegando à UI com `thresholdsSource: null`, e a
  // ficha do M6 imprimia o literal "null" no lugar do nome da fonte.
  const temCarta = r.param_atencao !== null || r.param_alerta !== null
    || r.param_emergencia !== null || (r.canais_com_carta?.length ?? 0) > 0;
  return {
    id: asInstrumentId(r.id),
    damId: asDamId(r.dam_id),
    code: r.code,
    type: r.type,
    label: r.label,
    easting: r.easting,
    northing: r.northing,
    lat: r.lat,
    lon: r.lon,
    levelNormalMax: r.level_normal_max,
    levelAtencaoMax: r.param_atencao,
    levelAlertaMax: r.param_alerta,
    levelEmergenciaMax: r.param_emergencia,
    thresholdsValidFrom: new Date(r.thresholds_valid_from),
    // GUPM-227: sem os `param_*` não há carta — `null` em vez de creditar uma
    // fonte que não produziu limiar nenhum. Quando há carta, credita a fonte real
    // gravada no cadastro (`gpx-inventario-v2`) em vez do literal antigo.
    thresholdsSource: temCarta ? r.thresholds_source : null,
    // GUPM-270 — canais com carta vigente. O tiltímetro nunca tem carta escalar
    // (`param_*` acima são todos nulos para ele): sem esta lista, `thresholdsSource`
    // sozinho o manteria "sem faixa" mesmo com as faixas por eixo carregadas.
    canaisComFaixa: r.canais_com_carta ?? [],
    section: r.section,
    cotaTopo: r.cota_topo,
    cotaFundo: r.cota_fundo,
    profundidade: r.profundidade,
    modoLeitura: r.modo_leitura,
    gpxArea: r.gpx_area,
    gpxEstrutura: r.gpx_estrutura,
    isActive: r.is_active,
    // GUPM-305 — status de publicação. Existia no banco desde o GUPM-228 e era
    // descartado aqui, então a UI não tinha como distinguir "medido e normal" de "mudo
    // exibindo a última leitura". `is_online` null (nunca derivado) é preservado como
    // null: colapsar em false diria "está mudo" de um instrumento sobre o qual nada
    // sabemos ainda.
    isOnline: r.is_online,
    lastReadingAt: r.last_reading_at ? new Date(r.last_reading_at) : null,
    // GUPM-307 — declaração de fora de serviço. Separada de `modoLeitura` de propósito:
    // aquela vem do inventário do fornecedor e diz como a leitura é COLETADA; esta é
    // decisão local do RT e é o único gate de "não espere leitura da API".
    foraDeServicoDesde: r.fora_de_servico_desde ? new Date(r.fora_de_servico_desde) : null,
    foraDeServicoMotivo: r.fora_de_servico_motivo,
    dataClass: r.data_class,
  };
}

function rowToReading(r: {
  id: string; instrument_id: string; dam_id: string; value: number; level: Reading['level'];
  observer_id: string | null; ts: string; origem: Reading['origem']; notes: string | null;
  data_class: Reading['dataClass']; canal: Canal | null;
}): Reading {
  return {
    id: asReadingId(r.id),
    instrumentId: asInstrumentId(r.instrument_id),
    damId: asDamId(r.dam_id),
    value: r.value,
    level: r.level,
    observerId: r.observer_id ? asEngineerId(r.observer_id) : null,
    ts: new Date(r.ts),
    origem: r.origem,
    notes: r.notes,
    dataClass: r.data_class,
    // GUPM-270 — a coluna existia desde o GUPM-125 e era descartada aqui. Sem ela
    // o app não distingue eixo A de eixo B nem de temperatura.
    canal: r.canal,
  };
}

// Formato de linha esperado por cada mapper. Usado com `.returns<T>()` para fixar o
// tipo do rowset: com o client sem generic Database, alguns filtros (ex.: `.not`) e
// embeds fazem o supabase-js inferir `{ [x: string]: any }`, que não casa com os
// parâmetros estritos de rowToX. A conversão explícita é a política deste arquivo.
type InstrumentRow = Parameters<typeof rowToInstrument>[0];
type ReadingRow = Parameters<typeof rowToReading>[0];

export const manualInstrumentacao: InstrumentacaoDataSource = {
  mode: 'manual',
  pendingIntegration: {
    owner: 'TetraTech',
    name: 'API de instrumentação geotécnica',
    eta: 'Pós-handover (ago/2026)',
    status: 'pendente',
    description:
      'Quando a API estiver disponível, as leituras dos PZs, INAs, MS, tiltímetros e demais ' +
      'sensores entrarão automaticamente. Enquanto isso, o M6 opera com inputs manuais.',
  },

  async listInstruments(opts) {
    // GUPM-129 — todo o app enxerga só instrumentos de fonte v2 (gpx_area setado).
    // Seed/mock tem gpx_area NULL e fica fora de todas as telas (M1/M2/M6/M6-gráfico).
    // GUPM-130 — lê `v_mapa_instrumentos` (inventário LEFT JOIN gpx_mapa) em vez de
    // `instruments` direto: coord/limiar de pino vêm do mapa quando há par; o filtro
    // `gpx_area not null` abaixo é redundante com o gate da própria view, mas mantido
    // para o contrato deste datasource ficar explícito e não depender só da view.
    let q = supabase
      .from('v_mapa_instrumentos')
      .select('*')
      .is('deleted_at', null)
      .not('gpx_area', 'is', null)
      .order('code');
    if (opts?.damId) q = q.eq('dam_id', opts.damId);
    const { data, error } = await q.returns<InstrumentRow[]>();
    if (error) throw error;
    return (data ?? []).map(rowToInstrument);
  },

  async getInstrument(id) {
    // GUPM-129 — mesma regra de fonte v2 do listInstruments: instrumento mock (gpx_area NULL)
    // não é exposto nem por id, para o choke point ser consistente (sem vazamento a caller futuro).
    // GUPM-130 — mesma view do listInstruments, pelo mesmo motivo de enriquecimento.
    const { data, error } = await supabase
      .from('v_mapa_instrumentos')
      .select('*')
      .eq('id', id)
      .not('gpx_area', 'is', null)
      .maybeSingle();
    if (error) throw error;
    return data ? rowToInstrument(data as InstrumentRow) : null;
  },

  async listLatestReadings({ instrumentId, damId, limit = 50, canal }) {
    // GUPM-129 — só leituras de instrumento de fonte v2. O inner join descarta as leituras
    // de instrumento mock (gpx_area NULL), que inflavam eventosUlt24h no M1. O objeto
    // `instruments` embutido serve só ao filtro; `rowToReading` mapeia apenas colunas de readings.
    let q = supabase
      .from('readings')
      .select('*, instruments!inner(gpx_area)')
      .not('instruments.gpx_area', 'is', null)
      .order('ts', { ascending: false })
      .limit(limit);
    if (instrumentId) q = q.eq('instrument_id', instrumentId);
    if (damId) q = q.eq('dam_id', damId);
    // GUPM-270 — `canal === undefined` não filtra (comportamento de antes);
    // `null` é filtro explícito por canal único. Sem isto a série do tiltímetro
    // viria com os três canais misturados numa curva só.
    if (canal !== undefined) q = canal === null ? q.is('canal', null) : q.eq('canal', canal);
    const { data, error } = await q.returns<ReadingRow[]>();
    if (error) throw error;
    return (data ?? []).map(rowToReading);
  },

  async listFaixasCanal(opts = {}) {
    let q = supabase.from('v_faixas_canal').select('*');
    if (opts.instrumentId) q = q.eq('instrument_id', opts.instrumentId);
    const { data, error } = await q.returns<{
      instrument_id: string; canal: Canal; valid_from: string;
      inicio_atencao: number; inicio_alerta: number; inicio_emergencia: number;
    }[]>();
    if (error) throw error;
    return (data ?? []).map((r) => ({
      instrumentId: r.instrument_id,
      canal: r.canal,
      validFrom: new Date(r.valid_from),
      inicioAtencao: r.inicio_atencao,
      inicioAlerta: r.inicio_alerta,
      inicioEmergencia: r.inicio_emergencia,
    }));
  },

  async listCurrentReadings(opts = {}) {
    // GUPM-136 — última leitura POR instrumento (view `readings_current`, já filtrada v2).
    // Diferente de `listLatestReadings` (top-N global por ts): não perde instrumento
    // de baixa cadência (evita esconder ALERTA de instrumento lento no mapa/semáforo).
    let q = supabase.from('readings_current').select('*');
    if (opts.instrumentId) q = q.eq('instrument_id', opts.instrumentId);
    if (opts.damId) q = q.eq('dam_id', opts.damId);
    const { data, error } = await q.returns<ReadingRow[]>();
    if (error) throw error;
    return (data ?? []).map(rowToReading);
  },

  async submitReading(input) {
    const { data, error } = await supabase
      .from('readings')
      .insert({
        instrument_id: input.instrumentId,
        value: input.value,
        ts: (input.ts ?? new Date()).toISOString(),
        source: input.origem ?? 'manual',
        origem: input.origem ?? 'manual',
        notes: input.notes ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return rowToReading(data);
  },
};

// ── Stub futuro (TetraTech API) ─────────────────────────────
// TODO[INTEGRACAO-TETRATECH]: quando o TI da TetraTech enviar:
//   1. URL base + token de auth
//   2. JSON Schema de evento de leitura
//   3. Política de retry/idempotência
// → criar Edge Function `supabase/functions/ingest-tetratech/` que
//   recebe webhook e popula readings. Trocar `manualInstrumentacao`
//   por uma impl `tetratechInstrumentacao` que muda apenas:
//   - mode: 'integrated'
//   - pendingIntegration: undefined
//   - submitReading: lança erro (entrada vem da API, não da UI).
//
// export const tetratechInstrumentacao: InstrumentacaoDataSource = { ... };
